import type { EntropyAudit, EntropySource, Orientation } from '@lilith-arcana-os/domain';

import { shannonEntropy, shannonMax, shannonRatio, previewEntropyHex } from './audit';
import { DEFAULT_TAROT_DECK_SIZE } from './bootstrap-draw';
import { drawOrientation } from './orientation';
import { bakersTransformShuffle, fisherYatesShuffle } from './shuffle';
import { uniformIndex, type RandomByteSource } from './uniform-index';

const MAX_CRYPTO_BYTES_PER_CALL = 65_536;

const resolveCrypto = (): Crypto => {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Web Crypto API is required for browser entropy.');
  }

  return globalThis.crypto;
};

const resolveSubtle = (): SubtleCrypto => {
  if (!globalThis.crypto?.subtle) {
    throw new Error('SubtleCrypto is required for browser entropy hashing.');
  }

  return globalThis.crypto.subtle;
};

const normalizeSource = (source?: EntropySource): EntropySource =>
  source === 'CSPRNG_LOCAL' ? 'CSPRNG_LOCAL' : 'WEB_CRYPTO';

const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');

export class BrowserEntropyEngine implements RandomByteSource {
  readonly source: EntropySource;

  public constructor(source?: EntropySource) {
    this.source = normalizeSource(source);
  }

  public getMode(): 'pwa_offline' | 'local_optional' {
    return this.source === 'CSPRNG_LOCAL' ? 'local_optional' : 'pwa_offline';
  }

  public getSource(): EntropySource {
    return this.source;
  }

  public async getBytes(length: number): Promise<Uint8Array> {
    if (!Number.isInteger(length) || length < 0) {
      throw new RangeError('length must be a non-negative integer.');
    }

    if (length === 0) {
      return new Uint8Array(0);
    }

    if (length > MAX_CRYPTO_BYTES_PER_CALL) {
      return this.getBytesChunked(length);
    }

    const bytes = new Uint8Array(length);
    resolveCrypto().getRandomValues(bytes);

    return bytes;
  }

  public async getBytesChunked(length: number): Promise<Uint8Array> {
    if (!Number.isInteger(length) || length < 0) {
      throw new RangeError('length must be a non-negative integer.');
    }

    const output = new Uint8Array(length);
    let offset = 0;

    while (offset < length) {
      const chunkLength = Math.min(MAX_CRYPTO_BYTES_PER_CALL, length - offset);
      const chunk = new Uint8Array(chunkLength);
      resolveCrypto().getRandomValues(chunk);
      output.set(chunk, offset);
      offset += chunkLength;
    }

    return output;
  }

  public async randomIndex(maxExclusive: number): Promise<number> {
    return uniformIndex(this, maxExclusive);
  }

  public async randomOrientation(): Promise<Orientation> {
    return drawOrientation(this);
  }

  public async shuffleDeck<T>(deck: T[]): Promise<T[]> {
    return fisherYatesShuffle(deck, this);
  }

  public async bakersTransform<T>(deck: T[], rounds = 3): Promise<T[]> {
    return bakersTransformShuffle(deck, () => new BrowserEntropyEngine(this.source), rounds);
  }

  public async sessionHash(input: {
    userId?: string | null;
    spreadId?: string | null;
    question?: string | null;
    timestamp?: number;
  }): Promise<string> {
    const payload = JSON.stringify({
      question: input.question ?? null,
      spreadId: input.spreadId ?? null,
      timestamp: input.timestamp ?? Date.now(),
      userId: input.userId ?? null,
    });
    const digest = await resolveSubtle().digest('SHA-256', new TextEncoder().encode(payload));

    return bytesToHex(new Uint8Array(digest));
  }

  public async generateAudit(input: {
    drawnCardIds: number[];
    bakerRounds: number;
    userId?: string | null;
    spreadId?: string | null;
    question?: string | null;
    sourceLabel?: string | null;
  }): Promise<EntropyAudit> {
    const timestamp = Date.now();
    const previewBytes = await this.getBytes(32);
    const sessionHash = await this.sessionHash({
      userId: input.userId ?? null,
      spreadId: input.spreadId ?? null,
      question:
        input.sourceLabel && input.sourceLabel.length > 0
          ? `${input.question ?? ''}::${input.sourceLabel}`
          : input.question ?? null,
      timestamp,
    });

    return {
      source: this.source,
      session_hash: sessionHash,
      shannon_entropy: shannonEntropy(input.drawnCardIds),
      shannon_max: shannonMax(DEFAULT_TAROT_DECK_SIZE),
      shannon_ratio: shannonRatio(input.drawnCardIds, DEFAULT_TAROT_DECK_SIZE),
      baker_rounds: input.bakerRounds,
      von_neumann_active: false,
      entropy_bytes_hex: previewEntropyHex(previewBytes),
      timestamp,
      lunar_phase: null,
      solar_decan: null,
      audit_mode: 'client',
      verifiable: false,
    };
  }
}
